import { AppLanguage } from '../services/language.service';
import { DEFAULT_TERMS, TermId, TermSet } from './terms';

/**
 * FE-236: maps `whitelabel.translationTerms` onto the vocabulary
 * TranslationService reads.
 *
 * Per the BE integration doc, `translationTerms` is stored as opaque JSON — the
 * server validates only that it is an object under 256 KB and never inspects the
 * keys. So this file IS the contract; there is no server-side schema backing it
 * up, and a malformed payload fails silently rather than erroring. Everything
 * below therefore validates defensively and falls back to platform wording
 * instead of throwing.
 *
 * Stored shape (locale-keyed, following the convention in the BE doc, extended
 * so a term can carry both numbers — one form per term cannot express
 * "Station"/"Stations"):
 *
 *   {
 *     "en": { "branch": { "one": "Station", "many": "Stations" } },
 *     "ar": { "branch": { "one": "محطة",   "many": "محطات"  } }
 *   }
 *
 * A bare string is also accepted and read as the singular, so the simpler shape
 * in the BE doc's example (`{ "en": { "branch": "Station" } }`) does not break —
 * the plural just stays platform wording.
 */

/** One term's forms as stored. A string is shorthand for the singular. */
export type StoredTermForms = string | { one?: string | null; many?: string | null };

/** `translationTerms` exactly as it comes back from /core/business/my-business. */
export type StoredTranslationTerms = Partial<Record<AppLanguage, Record<string, StoredTermForms>>>;

/** Valid term ids, for validating a payload or building the admin settings form. */
export const TERM_IDS = Object.keys(DEFAULT_TERMS) as TermId[];

export function isTermId(value: string): value is TermId {
  return value in DEFAULT_TERMS;
}

/**
 * Overlays the stored terms on the platform defaults, field by field.
 *
 * Falls back per FIELD rather than per term: a business that renames only the
 * plural still reads the platform singular, instead of a half-empty row
 * rendering as a blank label.
 */
export function toTermSet(stored: StoredTranslationTerms | null | undefined): TermSet {
  const terms: TermSet = structuredClone(DEFAULT_TERMS);
  if (!stored || typeof stored !== 'object') return terms;

  for (const language of ['en', 'ar'] as const) {
    const overrides = stored[language];
    if (!overrides || typeof overrides !== 'object') continue;

    for (const [term, forms] of Object.entries(overrides)) {
      if (!isTermId(term)) continue;            // unknown key: ignore, never throw
      const { one, many } = readForms(forms);
      const base = terms[term][language];
      terms[term] = {
        ...terms[term],
        [language]: { one: one ?? base.one, many: many ?? base.many },
      };
    }
  }

  return terms;
}

/** Accepts both the object form and the bare-string shorthand. */
function readForms(forms: StoredTermForms): { one: string | null; many: string | null } {
  if (typeof forms === 'string') return { one: clean(forms), many: null };
  if (!forms || typeof forms !== 'object') return { one: null, many: null };
  return { one: clean(forms.one), many: clean(forms.many) };
}

/** A field of spaces is a blank the admin left, not a term. */
function clean(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Byte size of a term map, for the admin form's counter.
 *
 * The BE cap is 256 KB of UTF-8 BYTES, not characters — Arabic costs 2 bytes per
 * character, so a map that looks well under the limit by `.length` can still be
 * rejected with WHITELABEL_TERMS_TOO_LARGE.
 */
export const TERMS_MAX_BYTES = 262144;

export function termsByteSize(terms: unknown): number {
  return new TextEncoder().encode(JSON.stringify(terms ?? {})).length;
}
